/**
 * Professors particulars (marketplace d'apren.ai, aprenAI/docs/marketplace-profes-pla.md §7.2).
 *
 * A examenselectivitat només hi ha la banda de l'alumne i les fitxes públiques dels profes; el backend és el
 * d'apren.ai (https://apren.ai/api/tutors/…, CORS obert). L'alta, el panell i els pagaments dels profes són a
 * apren.ai (/da-clases).
 *
 * L'alumne no té compte d'examenselectivitat per a això: en verificar el correu, apren.ai torna un token que es
 * desa a localStorage['aprenai_tutors_token'] i serveix per a /les-meves-solicituds.
 */
import { SUBJECTS, SUBJECT_NAMES, nodeIdOf, subjectPathFromName } from './aprenAiSubjects.mjs';

/** Interruptor: false = el web es veu igual que abans (cap franja, cap CTA; les rutes noves diuen «Molt aviat»).
    En local es pot encendre amb VUE_APP_PROFES_ON=1 (a producció no es posa: queda apagat) */
export const PROFES_ON = process.env.VUE_APP_PROFES_ON === '1';

/** Captació de profes (bàner per als que ja fan classes, «Dona classes» al peu): pot anar abans que la part de l'alumne.
    VUE_APP_PROFES_RECRUIT_ON=1 (o PROFES_ON, que l'inclou) */
export const PROFES_RECRUIT_ON = PROFES_ON || process.env.VUE_APP_PROFES_RECRUIT_ON === '1';

export const PROFES_API = process.env.VUE_APP_PROFES_API || 'https://apren.ai/api/tutors';
export const DONA_CLASSES_URL = 'https://apren.ai/da-clases?lang=ca&from=es';
const TOKEN_KEY = 'aprenai_tutors_token';

// ------------------------------------------------------------------ token de l'alumne

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch (e) {
    return null;
  }
}

export function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch (e) {
    // localStorage bloquejat: la sessió dura el que duri la pàgina
  }
}

// ------------------------------------------------------------------ API

export class ProfesError extends Error {
  constructor(code, status, data = {}) {
    super(code);
    this.code = code;
    this.status = status;
    this.data = data;
  }
}

/** fetch a https://apren.ai/api/tutors<path>. Llança ProfesError amb el codi del servidor ({error: 'code_wrong'}) */
export async function profesApi(path, { method = 'GET', body = null, auth = true } = {}) {
  const headers = { Accept: 'application/json' };
  const token = auth ? getToken() : null;
  if (token) headers.Authorization = token;
  if (body) headers['Content-Type'] = 'application/json';
  let res;
  try {
    res = await fetch(`${PROFES_API}${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
  } catch (e) {
    throw new ProfesError('network', 0);
  }
  let data = null;
  try {
    data = await res.json();
  } catch (e) {
    data = null;
  }
  if (!res.ok) throw new ProfesError((data && data.error) || 'server', res.status, data || {});
  return data;
}

/** Errors del servidor → text per a l'alumne */
const ERRORS = {
  network: "No hem pogut connectar. Comprova la connexió i torna-ho a provar.",
  server: "Alguna cosa ha fallat. Torna-ho a provar d'aquí a una estona.",
  email: "Aquest correu no sembla vàlid. Fes servir el teu correu habitual (no un de temporal).",
  too_many: "Massa intents seguits. Espera una estona i torna-ho a provar.",
  too_many_open: "Ja tens 3 sol·licituds obertes. Tanca'n alguna a «Les meves sol·licituds» abans de fer-ne una altra.",
  code_wrong: "El codi no és correcte. Revisa'l al correu.",
  code_expired: "El codi ha caducat. Torna a demanar-ne un.",
  guardian_required: "Si tens menys de 14 anys, cal el correu del teu pare, mare o tutor.",
  municipality_required: "Per a classes presencials, digues-nos el municipi.",
  message_short: "Explica una mica més què necessites (mínim 20 caràcters).",
  goals_required: "Tria què vols aconseguir (o explica-ho en una frase).",
  contact_in_text: "Treu el telèfon o el correu del missatge: el profe el rebrà quan acceptis la seva proposta.",
  name_required: "Escriu el teu nom.",
  birth_year: "Revisa l'any de naixement.",
  phone: "Aquest telèfon no sembla vàlid (ha de ser un número espanyol).",
  modality: "Tria si vols classes en línia, presencials o totes dues.",
  node: "Aquesta assignatura encara no té professors particulars.",
  node_required: "Tria l'assignatura.",
  tutor_not_found: "Aquest profe ja no està disponible. Pots enviar la sol·licitud a tots els profes de l'assignatura.",
  login_required: "La sessió ha caducat. Torna a obrir l'enllaç del correu.",
  not_found: "No hem trobat aquesta sol·licitud.",
  no_proposal: "Aquesta proposta ja no està disponible.",
  no_match: "Primer has d'acceptar la proposta del profe.",
  no_class: "Primer marca que ja heu fet classe.",
  rating: "Tria de 1 a 5 estrelles.",
};

export function errorMessage(e) {
  const code = e && (e.code || e.message);
  return ERRORS[code] || ERRORS.server;
}

// ------------------------------------------------------------------ assignatures

/** Node d'apren.ai d'una assignatura d'examenselectivitat (subject_path o nom) */
export function profesNodeId(subject) {
  return nodeIdOf(subjectPathFromName(subject));
}

export function subjectName(subjectPath) {
  return SUBJECT_NAMES[subjectPath] || null;
}

/** Les assignatures que tenen node a apren.ai (les úniques on hi pot haver profes), per ordre alfabètic */
export function profesSubjects() {
  return Object.keys(SUBJECTS)
    .filter(p => SUBJECT_NAMES[p])
    .map(p => ({ path: p, name: SUBJECT_NAMES[p], nodeId: nodeIdOf(p) }))
    .sort((a, b) => a.name.localeCompare(b.name, 'ca'));
}

export { subjectPathFromName };

// ------------------------------------------------------------------ format

export const LEVELS = [
  { value: 'eso', text: 'ESO' },
  { value: 'batx', text: 'Batxillerat' },
  { value: 'pau', text: 'Selectivitat' },
  { value: 'fp', text: 'FP' },
  { value: 'uni', text: 'Universitat' },
];

export function levelText(value) {
  const l = LEVELS.find(x => x.value === value);
  return l ? l.text : value;
}

export function formatPrice(cents) {
  if (!cents) return null;
  const eur = cents / 100;
  return `${Number.isInteger(eur) ? eur : eur.toFixed(2).replace('.', ',')} €/h`;
}

/** «de Química» / «d'Anglès» */
export function deApostrof(nom) {
  return /^[aeiouàèéíòóúh]/i.test(nom || '') ? `d'${nom}` : `de ${nom}`;
}

export function profesPath(subjectPath, muniSlug = null) {
  return `/professors-particulars/${subjectPath}${muniSlug ? `/${muniSlug}` : ''}`;
}

/** Mateix slug que apren.ai (profiles.slugify) */
export function slugify(s) {
  return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    .replace(/l·l/g, 'll').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80);
}

// ------------------------------------------------------------------ captació de profes

/**
 * Qui fa servir examenselectivitat com a profe particular: torna sovint (diversos dies), mira molts exàmens i sovint
 * de més d'una assignatura. Es compta només en aquest navegador (localStorage), sense enviar res enlloc.
 */
const SIGNAL_KEY = 'es_profe_signal';

export function recordProfeSignal(subjectPath) {
  try {
    const s = JSON.parse(localStorage.getItem(SIGNAL_KEY) || '{}');
    const day = new Date().toISOString().slice(0, 10);
    s.views = (s.views || 0) + 1;
    s.days = Array.from(new Set([...(s.days || []), day])).slice(-30);
    if (subjectPath) s.subjects = Array.from(new Set([...(s.subjects || []), subjectPath])).slice(-20);
    localStorage.setItem(SIGNAL_KEY, JSON.stringify(s));
  } catch (e) { /* navegació privada */ }
}

/** Diversos dies i molts exàmens, o moltes assignatures: el típic profe que prepara classes */
export function looksLikeProfe() {
  try {
    const s = JSON.parse(localStorage.getItem(SIGNAL_KEY) || '{}');
    const days = (s.days || []).length;
    const subjects = (s.subjects || []).length;
    const views = s.views || 0;
    return (days >= 3 && views >= 8) || (subjects >= 3 && views >= 6) || views >= 25;
  } catch (e) {
    return false;
  }
}
